/**
 * Cost Category Mixin
 * Provides dynamic cost category management for components
 */

import { mapGetters } from 'vuex';
import { getCostCategoryColor, getCostCategoryIcon } from '../utils/costCategoryIcons';

export default {
  computed: {
    ...mapGetters('billingApp', [
      'activeCostCategories',
      'costCategoriesLoading',
      'costCategoriesError',
    ]),

    costCategories() {
      return this.activeCostCategories;
    },

    /**
     * Cost category options formatted for v-select
     * Uses Master Data labels first and falls back to the code label.
     */
    costCategoryOptions() {
      return [...this.costCategories]
        .sort((a, b) => (
          Number(a.calculation_order || a.order || 0)
          - Number(b.calculation_order || b.order || 0)
        ))
        .map((category) => ({
          text: this.getMasterCategoryLabel(category),
          value: category.category_code || category.code,
        }));
    },
  },

  async created() {
    await this.loadCostCategories();
  },

  methods: {
    /**
     * Loads cost categories from API
     */
    async loadCostCategories() {
      await this.$store.dispatch('billingApp/fetchCostCategories');
    },
    getMasterCategoryLabel(category = {}) {
      const categoryCode = category.category_code || category.code;
      const masterLabel = category.category_name || category.name;
      if (masterLabel) {
        return masterLabel;
      }

      return this.formatChargeCodeLabel(categoryCode);
    },

    /**
     * Formats a charge code for display
     * @param {string} code - Category code
     * @returns {string} Formatted label
     */
    formatChargeCodeLabel(code) {
      if (!code) return 'Unknown Category';

      // Convert snake_case or UPPER_CASE to Title Case
      return code
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },

    /**
     * Formats cost category for display (backward compatibility)
     * @param {string} category - Category code
     * @returns {string} Formatted category name
     */
    formatCostCategory(category) {
      const dynamicCategory = this.costCategories.find(
        (cat) => (cat.category_code || cat.code) === category,
      );
      if (dynamicCategory) {
        return this.getMasterCategoryLabel(dynamicCategory);
      }

      return this.formatChargeCodeLabel(category);
    },

    /**
     * Gets category color for display
     * @param {string} category - Category code
     * @returns {string} Color name
     */
    getCategoryColor(category) {
      const dynamicCategory = this.costCategories.find(
        (cat) => (cat.category_code || cat.code) === category,
      );

      return getCostCategoryColor(dynamicCategory || category, this.costCategories);
    },

    /**
     * Gets category icon for display
     * @param {string} category - Category code
     * @returns {string} Icon name
     */
    getCategoryIcon(category) {
      return getCostCategoryIcon(category);
    },
  },
};
